import { parsePartialJson } from '@ai-sdk/ui-utils';
import { Message } from 'ai';
import { preprocessJSON } from 'shared/src/server/preprocess-json';
import type { SearchBrief } from 'shared/src/types/search-brief.type';
import { MAX_FOCUS_PRODUCTS, sanitizeFocusProducts } from 'shared/src/lib/focus-products';

export type TurnMode = 'discussion' | 'refine';
export type SearchScope = 'single' | 'kit';

/**
 * Budget the conversation AI extracted from the user's own words. `quantity`
 * is how many identical units the number covers ("900$ pour mes 4 pneus" → 4);
 * `stretch: false` only when the user ruled out going over at all ("not a
 * dollar more"). STREAMING CAVEAT: consumers MUST only act on `budget` from a
 * COMPLETED turn (`!isLoading`) — it is the trailing JSON field and a
 * truncated stream can legally parse `"max": 15` out of `1500`.
 */
export interface AssistantBudget {
  max: number;
  min?: number;
  currency?: string;
  quantity?: number;
  stretch?: false;
}

/**
 * One aspect of a side-by-side comparison. `values` is aligned with the
 * comparison's `items` (padded with '' while a row is still streaming);
 * `edge` is the index of the item that is better on this aspect, or null for
 * a tie or a matter of taste.
 */
export interface AssistantComparisonRow {
  label: string;
  values: string[];
  edge: number | null;
}

/**
 * The visual comparison an ANSWER turn attaches when the shopper compares
 * specific products ("difference between the iPad Pro and the Air"). Facts are
 * the conversation model's; the product photos, prices and links are ours,
 * matched from the catalogue by name (lib/focus-visuals.ts).
 */
export interface AssistantComparison {
  items: string[];
  rows: AssistantComparisonRow[];
  /** Who each item suits, aligned with `items`; [] until it streams. */
  bestFor: string[];
}

export interface AssistantParsed {
  isDone?: boolean;
  langCode?: string;
  obligatory?: boolean;
  message?: string | null; // Friendly per-turn preamble shown above the question
  prompt: string | null;
  searchQuery?: string;
  /**
   * The model's per-turn verdict: would a page answering the shopper's FIRST
   * message, as typed, still answer this turn's `searchQuery`? `false` only
   * when a later answer changed WHICH products qualify ("collant pour femme"
   * → "pour ballet"). Anything but an explicit `false` — absent, garbage, a
   * failed parse, a transcript from before the field — reads as `true`, which
   * is the shelf's behaviour from before the field existed.
   */
  openingStillFits: boolean;
  state?: string;
  suggestedUserAnswers: string[];
  turnMode?: TurnMode; // Per-turn AI-chosen interaction mode; absent on old transcripts → refine
  searchScope?: SearchScope; // Per-turn AI-chosen goal scope; absent on old transcripts → single
  confirmedElements: string[]; // Kit turns: cumulative user-confirmed element topics; absent → []
  searchBrief?: SearchBrief; // Conversational constraints/rejections; trailing field — read only on completed turns
  budget?: AssistantBudget; // User-stated budget; trailing field — read only on completed turns
  /**
   * The specific products this turn is about, as the shopper named them
   * ("iPad Pro", "iPad Air"); [] for a category conversation. The live shelf
   * shows these products first and drops pages that hold none of them.
   */
  focusProducts: string[];
  /** Side-by-side comparison on a turn that answers a comparison question. */
  comparison?: AssistantComparison;
  productReferences?: string[]; // For page question responses
  followUpSuggestions?: string[]; // For page question responses
}

// Tolerant of mid-stream partials ("d", "disc" → discussion). Returns undefined
// when the field is ABSENT (not yet streamed) so callers can distinguish "turnMode
// hasn't arrived" from an explicit "refine" and avoid flashing the refine layout
// before turnMode streams in (it's the 2nd JSON field, after "message"). A present
// non-discussion value normalizes to "refine"; callers default undefined → refine.
function normalizeTurnMode(v: unknown): TurnMode | undefined {
  if (typeof v !== 'string' || v.length === 0) return undefined;
  return 'discussion'.startsWith(v) ? 'discussion' : 'refine';
}

// Same partial-tolerance for searchScope ("k", "ki" → kit); absent/garbage → single,
// so pre-kit transcripts and single-item conversations behave exactly as before.
function normalizeSearchScope(v: unknown): SearchScope {
  return typeof v === 'string' && v.length > 0 && 'kit'.startsWith(v) ? 'kit' : 'single';
}

// Lenient shape guard: a finite positive max is the only hard requirement.
// Returns undefined for anything else (absent field, mid-stream garbage, null —
// the model is told to OMIT, not null).
function normalizeBudget(v: unknown): AssistantBudget | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  if (typeof o.max !== 'number' || !Number.isFinite(o.max) || o.max <= 0) return undefined;
  const budget: AssistantBudget = { max: o.max };
  if (typeof o.min === 'number' && Number.isFinite(o.min) && o.min > 0 && o.min < o.max) {
    budget.min = o.min;
  }
  if (typeof o.currency === 'string' && /^[A-Za-z]{3}$/.test(o.currency)) {
    budget.currency = o.currency.toUpperCase();
  }
  // One unit is the default and is dropped; past a couple dozen it is a
  // misread, not a set of identical things bought together.
  if (typeof o.quantity === 'number' && Number.isInteger(o.quantity) && o.quantity > 1 && o.quantity <= 24) {
    budget.quantity = o.quantity;
  }
  // Only an explicit "not a dollar over" turns the one exception off.
  if (o.stretch === false) budget.stretch = false;
  return budget;
}

// Shape the conversational brief for the UI thread: strings trimmed and capped,
// lists filtered to non-empty strings. Lenient by design — this is display-side
// shaping, NOT the security boundary; `sanitizeSearchBrief` on the server is.
// Same trailing-field caveat as budget: read only on completed turns.
function normalizeSearchBrief(v: unknown): SearchBrief | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const o = v as Record<string, unknown>;

  const text = (raw: unknown, max: number): string | undefined => {
    if (typeof raw !== 'string') return undefined;
    const trimmed = raw.trim().slice(0, max).trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };
  const list = (raw: unknown): string[] | undefined => {
    if (!Array.isArray(raw)) return undefined;
    const items = raw
      .filter((t): t is string => typeof t === 'string')
      .map((t) => t.trim().slice(0, 60).trim())
      .filter((t) => t.length > 0)
      .slice(0, 6);
    return items.length > 0 ? items : undefined;
  };

  const brief: SearchBrief = {};
  const useCase = text(o.useCase, 160);
  if (useCase) brief.useCase = useCase;
  const imageFindings = text(o.imageFindings, 200);
  if (imageFindings) brief.imageFindings = imageFindings;
  const audience = text(o.audience, 60);
  if (audience) brief.audience = audience;
  const mustHave = list(o.mustHave);
  if (mustHave) brief.mustHave = mustHave;
  const avoid = list(o.avoid);
  if (avoid) brief.avoid = avoid;

  return Object.keys(brief).length > 0 ? brief : undefined;
}

const MAX_COMPARISON_ROWS = 7;

const shortText = (raw: unknown, max: number): string =>
  typeof raw === 'string' ? raw.replace(/\s+/g, ' ').trim().slice(0, max).trim() : '';

// Tolerant of a comparison that is still streaming: an item list that has
// begun, rows whose values have not all arrived yet (padded with ''), a
// "bestFor" that is not there yet. Anything that could not render as a
// side-by-side table (fewer than two items) is dropped.
function normalizeComparison(v: unknown): AssistantComparison | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const o = v as Record<string, unknown>;
  const items = (Array.isArray(o.items) ? o.items : [])
    .map((item) => shortText(item, 48))
    .filter((item) => item.length > 0)
    .slice(0, MAX_FOCUS_PRODUCTS);
  if (items.length < 2) return undefined;

  const rows: AssistantComparisonRow[] = [];
  for (const raw of Array.isArray(o.rows) ? o.rows : []) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const row = raw as Record<string, unknown>;
    const label = shortText(row.label, 32);
    if (!label) continue;
    const given = Array.isArray(row.values) ? row.values : [];
    const values = items.map((_, i) => shortText(given[i], 80));
    const edge =
      typeof row.edge === 'number' && Number.isInteger(row.edge) && row.edge >= 0 && row.edge < items.length
        ? row.edge
        : null;
    rows.push({ label, values, edge });
    if (rows.length === MAX_COMPARISON_ROWS) break;
  }

  const bestForRaw = Array.isArray(o.bestFor) ? o.bestFor : [];
  const bestFor = bestForRaw.length > 0 ? items.map((_, i) => shortText(bestForRaw[i], 90)) : [];

  return { items, rows, bestFor: bestFor.some(Boolean) ? bestFor : [] };
}

export function parseAssistantMessage({ message }: { message: Message }): AssistantParsed {
  const content = preprocessJSON(message.content);
  const parsedMessage = parsePartialJson(content);
  // `parsePartialJson` returns an object with { value, state }
  // The "value" should contain your JSON fields if the parse was successful.
  const { value, state } = parsedMessage as {
    value: {
      message?: string;
      prompt?: string;
      suggestedUserAnswers?: string[];
      searchQuery?: string;
      openingStillFits?: unknown;
      isDone?: boolean;
      langCode?: string;
      obligatory?: boolean;
      turnMode?: string;
      searchScope?: string;
      confirmedElements?: unknown[];
      searchBrief?: unknown;
      budget?: unknown;
      focusProducts?: unknown;
      comparison?: unknown;
      productReferences?: string[];
      followUpSuggestions?: string[];
    } | null;
    state: string;
  };

  if (value && ['repaired-parse', 'successful-parse'].includes(state)) {
    return {
      message: value.message ?? null,
      prompt: value.prompt ?? null,
      searchQuery: value?.searchQuery,
      openingStillFits: value.openingStillFits !== false,
      state,
      isDone: value.isDone ?? false,
      langCode: value.langCode,
      obligatory: value.obligatory ?? false,
      suggestedUserAnswers: value.suggestedUserAnswers ?? [],
      turnMode: normalizeTurnMode(value.turnMode),
      searchScope: normalizeSearchScope(value.searchScope),
      confirmedElements: Array.isArray(value.confirmedElements)
        ? value.confirmedElements.filter((t): t is string => typeof t === 'string')
        : [],
      searchBrief: normalizeSearchBrief(value.searchBrief),
      budget: normalizeBudget(value.budget),
      focusProducts: sanitizeFocusProducts(value.focusProducts),
      comparison: normalizeComparison(value.comparison),
      productReferences: value.productReferences ?? [],
      followUpSuggestions: value.followUpSuggestions ?? [],
    };
  }
  return {
    message: null,
    prompt: null,
    openingStillFits: true,
    suggestedUserAnswers: [],
    confirmedElements: [],
    focusProducts: [],
    productReferences: [],
    followUpSuggestions: [],
  };
}

/**
 * The turn's `focusProducts`, but only once the array has finished streaming
 * (its closing bracket is in), so a half-written name ("iPad A") is never
 * looked up. [] until then, and for a turn that names no products.
 */
export function closedFocusProducts(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const match = preprocessJSON(raw).match(/"focusProducts"\s*:\s*(\[[^\]]*\])/);
  if (!match) return [];
  try {
    return sanitizeFocusProducts(JSON.parse(match[1]));
  } catch {
    return [];
  }
}

/**
 * Whether a streaming turn's `suggestedUserAnswers` array has closed. Every
 * field the shelf lookup reads streams before it (isDone, focusProducts,
 * prompt, searchQuery, openingStillFits, langCode, then the answers), so from
 * this point the lookup's inputs are final while the turn is still streaming
 * its trailing fields (confirmedElements, searchBrief, budget).
 */
export function closedSuggestedAnswers(raw: string | undefined | null): boolean {
  if (!raw) return false;
  const key = raw.match(/"suggestedUserAnswers"\s*:\s*\[/);
  if (!key || key.index === undefined) return false;
  let inString = false;
  for (let i = key.index + key[0].length; i < raw.length; i++) {
    const char = raw[i];
    if (inString) {
      if (char === '\\') i++;
      else if (char === '"') inString = false;
    } else if (char === '"') {
      inString = true;
    } else if (char === ']') {
      return true;
    }
  }
  return false;
}

/**
 * The shopper's opening words the live shelf may search next to the model's
 * refined query — or `undefined` when this turn's model judged they no longer
 * describe what qualifies (`openingStillFits: false`).
 *
 * Those words exist on the shelf for DRIFT: the refined query can lose the
 * word the shopper actually typed ("iPad pour prendre des notes…" → "student
 * laptop stylus note taking"), and the opening words keep those pages in
 * reach. When a later answer instead changed WHICH products qualify, a lift
 * from them undoes the answer — "collant pour femme" re-lifted fishnet and
 * maternity hosiery after "pour ballet" (2026-09-23). A preference the same
 * products can meet ("mal de dos", "16GB", a budget) keeps them.
 */
export function openingWordsForShelf(
  originalUserQuery: string | undefined,
  turn: Pick<AssistantParsed, 'openingStillFits'>,
): string | undefined {
  const opening = originalUserQuery?.trim();
  if (!opening) return undefined;
  return turn.openingStillFits === false ? undefined : opening;
}

/**
 * Parses a Message expecting its content to be a JSON array of strings.
 * It uses preprocessJSON and parsePartialJson to recover from minor errors.
 * In case parsing fails (or the state is not "successful" or "repaired"),
 * it falls back to a regex-based extraction that only returns complete strings.
 *
 * For example, given:
 *   ["one", "two", "th,
 * it will return:
 *   ["one", "two"]
 */
export function parseSuggestionsMessage(message: Message): string[] {
  const content = preprocessJSON(message.content);
  const parsedResult = parsePartialJson(content);
  const { value, state } = parsedResult as {
    value: unknown;
    state: string;
  };

  if (value && ['successful-parse', 'repaired-parse'].includes(state)) {
    // If the parsed value is an array, filter out any non-string values.
    if (Array.isArray(value)) {
      return value.filter((item) => typeof item === 'string');
    }
  }

  // Fallback: use regex extraction to only get complete string literals.
  return extractCompleteStrings(content);
}

/**
 * Strips the conventional "[User's answer to …]: <text>" wrapper added by the
 * search-bar when sending follow-up answers, so consumers (persisted
 * transcripts, the scrollback history panel) show the clean human input.
 */
export function unwrapUserInput(raw: string): string {
  if (!raw) return '';
  // Matches "[…]: actual text"
  const match = raw.match(/^\[[^\]]*\]\s*:\s*(.+)$/s);
  return (match?.[1] ?? raw).trim();
}

/**
 * Extracts complete string literals from the provided input.
 * Incomplete strings (i.e. those without a closing quote) are ignored.
 *
 * The regex works as follows:
 * - It looks for a starting double quote.
 * - Then it captures any sequence of characters that are either an escaped character or any character that isn't a double quote or backslash.
 * - It finally requires a closing double quote.
 */
function extractCompleteStrings(input: string): string[] {
  const regex = /"((?:\\.|[^"\\])*)"/g;
  const suggestions: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(input)) !== null) {
    suggestions.push(match[1]);
  }

  return suggestions;
}
